import { useLocation } from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import { DemandeFonctionnaliteProvider } from "@hakili/ui";

import { holdsSettlementsRight } from "./routes/settlements-right.js";
import { PorteeBanner } from "./shell/PorteeBanner.js";
import { useEffect, useState } from "react";

import { Choreographie } from "./routes/choreographie.js";
import { InvitationCard } from "./routes/invitation.js";
import { Invitation } from "./routes/invitation-publique.js";
import { DiagnosticDrapeaux } from "./routes/diagnostic-drapeaux.js";
import { Connexion } from "./routes/connexion.js";
import { DossiersScreen } from "./routes/dossiers.js";
import { DossierScreen } from "./routes/dossier.js";
import { ArriveesScreen } from "./routes/arrivees.js";
import { RecommandationsScreen } from "./routes/recommandations.js";
import { Cabinets } from "./routes/cabinets.js";
import {
  PartnerAddModal,
  PartnerTabHeader,
  PartnerTabInWords,
  PartnerTabs,
  partnerTabOf,
  useAddPartner,
  type PartnerTab,
} from "./routes/partner-tabs.js";
import { DeveloppementScreen } from "./routes/developpement.js";
import { ExpertisesScreen } from "./routes/expertises.js";
import { FonctionnalitesScreen } from "./routes/fonctionnalites.js";
import { PrioritesScreen } from "./routes/priorites.js";
import { SupervisionScreen } from "./routes/supervision.js";
import { TeamScreen } from "./routes/team.js";
import { BanksTab } from "./routes/partner-banks.js";
import { PartnerSheet } from "./routes/partner-sheet.js";
import { Accounts } from "./routes/accounts.js";
import { Dealerships, type Partner } from "./routes/dealerships.js";
import { Partenaires } from "./routes/partenaires.js";
import { Profil } from "./routes/profil.js";
import { useSession } from "./session/useSession.js";
import { ConsoleShell, resoudre } from "./shell/ConsoleShell.js";
import { creerConsoleStore } from "./shell/store.js";
import { cheminPublic, VUES, type Vue } from "./shell/views.js";

/**
 * `P0-UIK-021`: the console, assembled.
 *
 * **Everything here already existed.** `P0-UIK-013` built the shell
 * composition, `014` the console's chrome and its route table, `015` the login
 * and profile screens, `018` the demo gate, `020` the bundler. Each ticked
 * green on specs that rendered a component in isolation, and the deploy sitting
 * of 24 August 2026 found that no page mounted any of them: `main.tsx` opened a
 * React root and rendered `null`. This file is the sentence that was missing,
 * and it is deliberately short , if assembling six finished parts needed a
 * hundred lines of new logic, the parts would be the wrong shape.
 *
 * **The view is derived, never chosen.** `WF-CON-0`: *"derive the view from the
 * user's role"*. `/me` answers `view.viewId`; an unknown one falls back to the
 * NARROWEST view rather than the widest, because the failure mode of a wrong
 * guess is asymmetric , too few screens is a support call, too many is a
 * disclosure. The client-side whitelist is never the thing that refuses in any
 * case: 6.1.8's four layers run on the server.
 */

/** The narrowest view of 03, 2.0: two screens. The default when nothing better is known. */
const VUE_PAR_DEFAUT: Vue = "collecte";

/**
 * The screens that EXIST today, and the reason an authenticated visitor lands
 * on `profil`.
 *
 * The founder's sitting of 27 August 2026 signed in and arrived at a
 * placeholder: `resoudre` sends an unknown path to the VIEW's landing screen,
 * which for `administration` is `collecte` at `/dossiers`, and `collecte` has
 * no screen behind it yet. Canon's landing rule is right and stays; what was
 * missing is that the console must not seat a person somewhere nothing is
 * built. So a resolution naming an unbuilt screen is redirected here, and this
 * set is the ONLY place that knowledge lives , a screen item deletes its own
 * line from it and the landing follows, with nothing else to remember.
 */
// `P0-CNW-001` adds `partenaires`. The rail and the redirect both read this
// set, so a chip and a landing can never disagree about what exists.
/**
 * `round CN-A`, `CN-A.1`: `collecte` joins , the Dossiers list and, at
 * `/dossiers/:id`, the file. The route table already binds both paths to the
 * one screen (`views.ts`: a view granted the list is granted the file).
 */
export const ECRANS_CONSTRUITS: ReadonlySet<string> = new Set([
  "profil",
  "partenaires",
  "collecte",
  "arrivees",
  "expertises",
  // `round CN-B`, `CN-B.1`: « Équipe »; `CN-B.3`: « Comptes ».
  "equipe",
  "comptes",
  // `round CN-C`: the console's last screens.
  "priorites",
  "developpement",
  "supervision",
  "fonctionnalites",
]);

const estVue = (value: string | null): value is Vue =>
  value !== null && (VUES as readonly string[]).includes(value);

/**
 * The application takes its origin AND, optionally, a `fetch`.
 *
 * `useSession` has accepted a stub since `P0-UIK-021`; the application did not
 * pass one, so a spec that mounted `App` reached the real network and every
 * answer was a failure , which looks like a correct anonymous render and is
 * not. dealer-web's own `App` takes both for the same reason.
 */
export function App({
  origin,
  expertOrigin,
  clientOrigin,
  fetchImpl,
}: {
  readonly origin: string;
  /** `C5.0.0(a)`: where the cabinet's page lives, per environment; the sheet's link is absolute on it. */
  readonly expertOrigin: string;
  /** `C7.3`: where the client's app lives, per environment; the deposit link is absolute on it. */
  readonly clientOrigin: string;
  readonly fetchImpl?: typeof globalThis.fetch;
}): React.JSX.Element {
  // **Asked of the path BEFORE the session**, and `P0-UIK-024` is why: the boot
  // read below must not run on a route whose authorisation is its own token.
  const { path, search, naviguer } = useLocation();
  const publique = cheminPublic(path);

  const {
    session,
    queryClient,
    connecter,
    deconnecter,
    submitFeatureRequest,
    listDossiers,
    readSupervision,
    readFileFees,
    collectFileFee,
    readTopups,
    confirmTopup,
    readSettlements,
    observeHakiliLeg,
    prioritesDoors,
    developpementDoors,
    supervisionDoors,
    fonctionnalitesDoors,
    markDossiersSeen,
    dossierDoors,
    arriveesDoors,
    recommandationsDoors,
    expertisesDoors,
    cabinetsDoors,
    teamDoors,
    banksDoors,
    partnerSheetDoors,
    accountsDoors,
    paymentSettingsDoors,
    holdTermsDoors,
    breachTermsDoors,
    mileageToleranceDoors,
    enregistrer,
    listPartners,
    ouvrirConcession,
    nommerAdministrateur,
    renvoyerInvitation,
    revokeInvitationLink,
    readPartnerAccount,
    writePartnerAddress,
    readCities,
    readCityZones,
    lireLeCode,
    lireLeLien,
    lireLesDrapeaux,
  } = useSession(origin, fetchImpl, { amorcer: !publique });

  /**
   * `P0-CNW-028`, `00-SPINE` 6.6's second layer: **this surface admits Hakili's
   * staff and nobody else.**
   *
   * Two refusals, and they are one rule read twice. `organisationType` is the
   * question the api has always answered and nothing read. `view === null` is
   * the api saying this role has no console view at all , which it says
   * correctly for `responsableConcession`, and which `App` used to answer by
   * falling back to `collecte` and printing that view's rail and its scope
   * label beside somebody else's role.
   */
  const authentifie = session.status === "authenticated" ? session.identity : null;
  const admis =
    authentifie !== null && authentifie.organisationType === "hakili" && authentifie.view !== null;
  const identite = admis ? authentifie : null;
  const refusDeSurface = authentifie !== null && !admis ? t("common.surface.refusConsole") : undefined;
  const declaree = identite?.view ?? null;
  const vueDuRole: Vue = estVue(declaree) ? declaree : VUE_PAR_DEFAUT;

  // The demonstrated view (`P0-UIK-018`), which in production can never differ
  // from the role's: the switcher that would change it does not render there.
  const [vueChoisie, setVueChoisie] = useState<Vue | null>(null);
  const vue = vueChoisie ?? vueDuRole;

  const [store] = useState(() => creerConsoleStore(vueDuRole));
  /**
   * `CN-B.3`, WF-CON-43's acceptance criterion: « Une idée ? » on Profil names
   * the screen the person was ON before, never « Profil ». Remembered as the
   * path changes; the profile's own button reads it.
   */
  const [previousScreen, setPreviousScreen] = useState<string | null>(null);
  useEffect(() => {
    const now = resoudre(vue, path);
    const screen = now.kind === "render" ? now.route.screen : null;
    if (screen !== null && screen !== "profil") setPreviousScreen(screen);
  }, [vue, path]);
  useEffect(() => {
    store.getState().setPersona(vue);
  }, [store, vue]);

  /**
   * The dealership the fiche is about.
   *
   * **`P0-CNW-029` is the real selection Round B's comment promised.** It was
   * set by `Partenaires`' own `onOuverte` and by nothing else, so the fiche was
   * reachable for the dealership opened in THIS page load and for no other , a
   * founder who reloaded could name nobody's administrator. The directory below
   * sets it too, from a row.
   */
  // `round CN-B`, `CN-B.2`: the fiche is a ROUTE now (`/partenaires/:orgId`),
  // reachable after a reload for any partner the door lists; the selection a
  // page load used to hold is the URL's.

  /**
   * The partner directory, read once when the screen that shows it is seated.
   *
   * `null` means "not answered yet", which `Dealerships` renders as loading; an
   * empty array means the door answered and there are none. The two are
   * different sentences on the screen and are kept different here.
   *
   * **Re-read after a dealership is opened**, because opening one is the one
   * gesture on this screen that changes the list, and a directory that did not
   * move would tell a founder their own action did nothing.
   */
  const [partners, setPartners] = useState<readonly Partner[] | null>(null);
  const [directoryEpoch, setDirectoryEpoch] = useState(0);

  const resolution = resoudre(vue, path);
  /**
   * `round CN-B`, `CN-B.2`, WF-CON-35's scope rule: a relation view sees ITS
   * type and nothing else, the responsable the insurers, the administration
   * all four with the manager named. The tab in the URL is honoured only among
   * the tabs the view holds; otherwise the view's first.
   */
  const tabsOfView: readonly PartnerTab[] =
    vue === "banques"
      ? ["banques"]
      : vue === "concess"
        ? ["concessionnaires"]
        : vue === "resp"
          ? ["assureurs"]
          : ["banques", "concessionnaires", "experts", "assureurs"];
  const askedTab = partnerTabOf(new URLSearchParams(search).get("onglet"));
  const partnerTab: PartnerTab = tabsOfView.includes(askedTab) ? askedTab : (tabsOfView[0] ?? "banques");
  const addDealer = useAddPartner();
  const ecran = resolution.kind === "render" ? resolution.route.screen : null;
  // `CN-B.2`, WF-CON-36: the fiche, reached from a row at `/partenaires/:orgId`.
  const sheetId =
    ecran === "partenaires" && resolution.kind === "render" ? resolution.params["orgId"] : undefined;
  // The drawing draws its scope banner under a LIST's heading (Dossiers,
  // Priorités, Développement) and never over a file: the file's own header is
  // the person's name and the stage, and the scope was read on the way in.
  const surUnDossier =
    ecran === "collecte" && resolution.kind === "render" && resolution.params["id"] !== undefined;
  // The drawing draws the scope banner under SEVEN headings , Dossiers,
  // Arrivées, Priorités, Développement, Partenaires, Équipe, Comptes , and
  // not on Expertises, Supervision, Fonctionnalités, Profil, nor a file. The
  // pair of `CN-A.3` found it on Expertises, where the drawing has none.
  const screenWithScope = new Set([
    "collecte",
    "arrivees",
    "priorites",
    "developpement",
    "partenaires",
    "equipe",
    "comptes",
  ]).has(ecran ?? "");

  // Read only when the screen that needs it is the one on the table: the
  // directory is `/partenaires`' data, and fetching it from `/profil` would be
  // a request nobody's screen is waiting for.
  // `round CN-H`, `M0(c)`: the directory's door is `GET /organisations`,
  // `06` §6.1.5 row 11 `animerPortefeuillePartenaires` , « non » for the
  // responsable des dossiers, whose Partenaires is the INSURERS alone
  // (WF-CON-35: « responsable des dossiers (insurers only) »), a tab that is
  // words until the insurance round. Her screen asked the door anyway and the
  // corridor read the refusal: the door is asked only by a view whose tabs
  // read it. The insurers' own door will be row 12's, that round's work.
  const readsTheDirectory = vue !== "resp";
  useEffect(() => {
    // `CN-B.3`: « Comptes » lists the organisations from the same door.
    if (identite === null || !readsTheDirectory || (ecran !== "partenaires" && ecran !== "comptes")) return;
    // An object and not a `let`, which is `useSession.ts`'s own idiom two files
    // away: the compiler cannot see a closure reassign a local, narrows it to
    // `true`, and the guard becomes an unnecessary condition , a lint error
    // that is really the type system telling you the flag does not work.
    const run = { live: true };
    void (async () => {
      const answered = await listPartners();
      if (run.live) setPartners(answered);
    })();
    return () => {
      run.live = false;
    };
  }, [identite, ecran, readsTheDirectory, listPartners, directoryEpoch]);

  /**
   * The redirect, performed in an effect and never during render.
   *
   * `resoudre` answers before anything mounts , that is why `ConsoleShell`
   * exports it , and a redirect issued while rendering has already mounted the
   * screen it was meant to prevent. Unknown paths land here too, which is how
   * this router cooperates with `P0-INF-032`'s SPA fallback: CloudFront serves
   * `index.html` with a `200` for any path, the application reads the path, and
   * a path it does not know resolves IN the app to the view's landing screen.
   */
  useEffect(() => {
    if (identite === null) return;
    // `round CN-A`, `CN-A.0`'s order, ruled on 11 September: **each view lands
    // where the drawing lands it** , `03` §2.0's first screen of its list ,
    // and a screen nobody has built shows the drawing's own sentence, naming
    // its round, IN PLACE. Round 191 sent every unbuilt landing to `/profil`
    // because the placeholder of 27 August named no round; the sentence now
    // does, and a relation view standing on « Priorités … porté par CN-B » is
    // standing on the truth. Only a path the resolver does not know, or one
    // outside the view's whitelist, is redirected , to the VIEW's own landing,
    // the resolver's answer. `history.pushState` keeps it in-app: the SPA
    // fallback already served the document.
    if (resolution.kind === "redirect" && path !== resolution.to) naviguer(resolution.to);
  }, [identite, resolution, path, naviguer]);

  return (
    /**
     * `round CN-A`, `CN-A.0`: **« Une idée ? » is live from here on.**
     *
     * `@hakili/ui` has carried the modal and its three questions since round
     * 203; `shell/demande.ts` has carried the context since `P0-UIK-015`. What
     * was missing was the DOOR, so nothing a person wrote had anywhere to go.
     * The provider wraps the whole shell because `WF-CON-24` puts the control
     * in the rail: reachable from every screen, on every view.
     */
    <DemandeFonctionnaliteProvider
      envoyer={(demande) =>
        submitFeatureRequest({
          faire: demande.faire,
          manque: demande.manque,
          permettrait: demande.permettrait,
          contexte: demande.contexte,
        })
      }
    >
      <ConsoleShell
        vue={vue}
        demo={{ serveur: false, productionBuild: import.meta.env.PROD }}
        choisirVue={setVueChoisie}
        queryClient={queryClient}
        store={store}
        // A principal this surface will not admit is ANONYMOUS to it: the gate
        // renders the sign-in with the reason, rather than a shell it has no
        // right to. The session itself is untouched , it belongs to the surface
        // it was opened on, which is the other half of 6.6.
        status={authentifie !== null && !admis ? "anonymous" : session.status}
        /**
         * `round CN-A`, `CN-A.0`: the person, for the rail's initials and the
         * scope banner's first words. From `/me` and never from a table , the
         * drawing stores a name beside each demonstration view, and a product
         * cannot: the name is the door's.
         */
        personne={identite?.fullName ?? ""}
        path={path}
        search={search}
        pending={<p className="p-6 text-sm text-gray-500">{t("console.session.chargement")}</p>}
        signIn={
          <Connexion
            connecter={connecter}
            {...(refusDeSurface === undefined ? {} : { motif: refusDeSurface })}
          />
        }
        // The conversation panel is `P1-CNW`'s, and an empty aside is worse than
        // none: it would take a quarter of the width to say nothing.
        renderPanel={() => null}
        // `P1-CNW-019`. The rail asks the same set the redirect asks, so a chip
        // and a landing can never disagree about what exists.
        construits={ECRANS_CONSTRUITS}
        deconnecter={deconnecter}
      >
        {/**
         * `round CN-A`, `CN-A.0`: **the scope banner, above every screen.**
         *
         * `hakili-console.jsx`'s `PorteeBanner` sits at the top of the content
         * column, not inside one screen, because it is a property of WHO IS
         * LOOKING and not of what they are looking at. It renders nothing on a
         * public route , an invitation's holder has no view yet , and nothing
         * for the administration, whose `portee` the drawing leaves `null`.
         */}
        {publique || identite === null || surUnDossier || !screenWithScope ? null : (
          <PorteeBanner vue={vue} personne={identite.fullName} />
        )}
        {publique ? (
          <Invitation
            // `publique` now comes from the PATH rather than the resolution, so
            // the compiler no longer narrows for us. Asked once, here.
            token={resolution.kind === "render" ? (resolution.params["token"] ?? "") : ""}
            origin={origin}
            {...(fetchImpl === undefined ? {} : { fetchImpl })}
            onAccepted={() => {
              // The console's own landing is derived from the view, and the view
              // comes from `/me` , which this caller has not read yet. Sending
              // them to the sign-in they now have a password for is the honest
              // answer, and it is one navigation rather than a guess.
              naviguer("/");
            }}
          />
        ) : identite !== null &&
          ecran === "collecte" &&
          resolution.kind === "render" &&
          resolution.params["id"] !== undefined ? (
          <DossierScreen
            vue={vue}
            meId={identite.id}
            dossierId={resolution.params["id"]}
            {...dossierDoors}
            // `P1-CNW-064` (D-1031-18): the dealer-access facts are `voirEtRelancerDossiers`'s door; a seat that does not hold the right is handed no door, so no panel is drawn and nothing is asked.
            readDealerAccessFacts={
              identite.rights["voirEtRelancerDossiers"] === "oui"
                ? dossierDoors.readDealerAccessFacts
                : undefined
            }
            clientOrigin={clientOrigin}
            onBack={() => {
              // `WF-CON-02` step 4: the band's filter survives the round trip ,
              // it was in the URL on the way in and it is in the URL on the way back.
              naviguer(search === "" ? "/dossiers" : `/dossiers${search}`);
            }}
            onGoExpertises={() => {
              naviguer("/expertises");
            }}
          />
        ) : identite !== null && ecran === "priorites" ? (
          // `round CN-C`, `CN-C.1`, WF-CON-38: the signals derived by the door
          // over the caller's own portfolio; the fiche opened by its route.
          <PrioritesScreen
            // `K-1`, `M3 (d)`: the reservation signals are `dossier`'s door under row 1
            // `voirEtRelancerDossiers`, which the relation chairs do not hold; a screen asks
            // only the doors its chair holds (CN-H), so the block exists for the right's holder.
            {...(identite.rights["voirEtRelancerDossiers"] === "oui"
              ? prioritesDoors
              : { readSignals: prioritesDoors.readSignals, logExchange: prioritesDoors.logExchange })}
            vue={vue}
            onOpenPartner={(organisationId) => {
              naviguer(`/partenaires/${organisationId}`);
            }}
            onOpenDossier={(dossierId) => {
              naviguer(`/dossiers/${dossierId}`);
            }}
          />
        ) : identite !== null && ecran === "developpement" ? (
          // `CN-C.2`, WF-CON-39.
          <DeveloppementScreen {...developpementDoors} vue={vue} />
        ) : identite !== null && ecran === "supervision" ? (
          // `CN-C.3`, WF-CON-41: the administration alone, by the rail and the door.
          <SupervisionScreen {...supervisionDoors} />
        ) : identite !== null && ecran === "fonctionnalites" ? (
          // `CN-C.4`, WF-CON-42: the administration alone.
          <FonctionnalitesScreen {...fonctionnalitesDoors} />
        ) : identite !== null && ecran === "comptes" ? (
          // `round CN-B`, `CN-B.3`, WF-CON-40: the administration alone; a
          // relation view is refused by the rail (`views.ts`) and by the door.
          <Accounts
            {...accountsDoors}
            // `round 9`, `MINT-ERASURE-RIGHT`: the erasure has its own right (06 §6.1.5 row 33), narrower than the
            // screen's; a seat that reaches this screen without it is shown the gesture greyed, with the reason.
            mayErase={identite.rights["effacerComptesClients"] === "oui"}
            // Partner accounts, `M1`: the override to deactivate any partner account is the administration's.
            mayDeactivate={identite.rights["administrerComptesPartenaires"] === "oui"}
            // Partner accounts, `M2`: the erasure of a partner's user has its own right (row 34), narrower than that one.
            mayErasePartner={identite.rights["effacerComptesPartenaires"] === "oui"}
            partners={partners}
            onOpened={() => {
              setDirectoryEpoch((epoch) => epoch + 1);
            }}
          />
        ) : identite !== null && ecran === "equipe" ? (
          <TeamScreen
            {...teamDoors}
            vue={vue}
            // 06 §6.1.5 row 23: creating a Hakili account is the administration's
            // alone; the responsable's invite control is greyed with that reason.
            mayInvite={identite.rights["gererComptesHakili"] === "oui"}
          />
        ) : identite !== null && ecran === "expertises" ? (
          <ExpertisesScreen {...expertisesDoors} vue={vue} expertOrigin={expertOrigin} />
        ) : identite !== null && ecran === "arrivees" ? (
          <ArriveesScreen
            {...arriveesDoors}
            onGoTeam={() => {
              naviguer("/equipe");
            }}
          />
        ) : identite !== null &&
          ecran === "collecte" &&
          resolution.kind === "render" &&
          resolution.route.path === "/recommandations" ? (
          // `WF-CON-11-NEW-BANK-PIPELINE`, `M1`: the chargé's recommendations , a detail of `collecte`, so the
          // view that holds the list holds this too; never a screen of the bank's own app (I8).
          <RecommandationsScreen
            {...recommandationsDoors}
            onOpenDossier={(id) => {
              naviguer(`/dossiers/${id}`);
            }}
          />
        ) : identite !== null && ecran === "collecte" ? (
          <DossiersScreen
            vue={vue}
            // `WF-CON-11-NEW-BANK-PIPELINE`, `M2`: the count of sends waiting on this seat, asked only where
            // the seat holds `soumettreABanque` , the door that would refuse the rest is never called.
            {...(identite.rights["soumettreABanque"] === "oui"
              ? {
                  readPendingRecommendations: async () => (await recommandationsDoors.readProposals()).length,
                  onOpenRecommendations: () => {
                    naviguer("/recommandations");
                  },
                }
              : {})}
            listDossiers={listDossiers}
            readSupervision={readSupervision}
            readFileFees={readFileFees}
            collectFileFee={collectFileFee}
            readTopups={readTopups}
            confirmTopup={confirmTopup}
            readSettlements={holdsSettlementsRight(vue) ? readSettlements : undefined}
            observeHakiliLeg={observeHakiliLeg}
            markDossiersSeen={markDossiersSeen}
            onOpen={(id) => {
              naviguer(`/dossiers/${id}`);
            }}
            // `WF-CON-02` step 4: the band's filter survives a round trip into a
            // dossier, so it lives in the URL and not in a component that
            // unmounts on the way.
            stageFilter={new URLSearchParams(search).get("etape")}
            onStageFilter={(stage) => {
              naviguer(stage === null ? "/dossiers" : `/dossiers?etape=${encodeURIComponent(stage)}`);
            }}
          />
        ) : identite !== null && ecran === "partenaires" && sheetId !== undefined ? (
          // `round CN-B`, `CN-B.2`, WF-CON-36: the fiche, live where a module
          // answers, in words where none does; the concession keeps its own
          // blocks below the drawing's.
          ((): React.JSX.Element => {
            // `M0(c)`: the responsable's portfolio is the insurers', whose fiche
            // is the insurance round's; no directory was read for her.
            if (!readsTheDirectory) return <PartnerTabInWords tab="assureurs" />;
            const partner = partners?.find((one) => one.id === sheetId) ?? null;
            if (partners === null)
              return <p className="text-sm text-gray-500">{t("common.state.chargement")}</p>;
            if (partner === null) {
              return (
                <p data-testid="partenaire-inconnu" className="text-sm text-gray-500">
                  {t("console.partenaires.ficheInconnue")}
                </p>
              );
            }
            return (
              <PartnerSheet
                {...partnerSheetDoors}
                vue={vue}
                partner={partner}
                // 06 §6.1.5 row 15: the administration and the relation banques.
                mayAdjustPrefilter={identite.rights["ajusterPrefiltreBanque"] === "oui"}
                mayRecordConvention={identite.rights["parametrerCriteresBanque"] === "oui"}
                mayConfigureCriteria={identite.rights["parametrerCriteresBanque"] === "oui"}
                // `PRET-BANKS`: the panel the filter walks is `ouvrirOrganisation`'s, the same right that opens a bank.
                mayAdmitToPanel={identite.rights["ouvrirOrganisation"] === "oui"}
                // `T-1c`: row 11; the portfolio's membership is the door's own refusal.
                mayRecordDealerConvention={identite.rights["animerPortefeuillePartenaires"] === "oui"}
                // `K-2` (resumed): the settlement and the waiver are row 24's, the administration alone.
                mayActOnClaims={identite.rights["accederDonneesFinancieres"] === "oui"}
                onBack={() => {
                  naviguer(
                    `/partenaires?onglet=${partner.type === "banque" ? "banques" : partner.type === "concessionnaire" ? "concessionnaires" : partner.type === "cabinet" ? "experts" : "assureurs"}`,
                  );
                }}
                concession={{
                  nommerAdministrateur: (saisie) => nommerAdministrateur(partner.id, saisie),
                  renvoyer: renvoyerInvitation,
                  lireAdresse: readPartnerAccount,
                  ecrireAdresse: writePartnerAddress,
                  lireVilles: readCities,
                  lireLieux: readCityZones,
                }}
              />
            );
          })()
        ) : identite !== null && ecran === "partenaires" ? (
          // The founder's finding on row 39: the drawing's four tabs, the tab
          // in the URL (`?onglet=`); `CN-B.2`: behind the founder's bar for
          // every view, each seeing its own type (WF-CON-35).
          <div className="flex flex-col gap-4">
            <PartnerTabs
              active={partnerTab}
              tabs={tabsOfView}
              subtitle={
                vue === "resp" ? t("console.partenaires.sousTitreResp") : t("console.partenaires.sousTitre")
              }
              onSelect={(tab) => {
                naviguer(`/partenaires?onglet=${tab}`);
              }}
            />
            {partnerTab === "banques" ? (
              <BanksTab
                {...banksDoors}
                vue={vue}
                partners={partners}
                mayOpen={identite.rights["ouvrirOrganisation"] === "oui"}
                mayInvite={identite.rights["inviterPremierResponsable"] === "oui"}
                {...(cabinetsDoors.inviteResponsible === undefined
                  ? {}
                  : { inviteResponsible: cabinetsDoors.inviteResponsible })}
                // `FX-3`, `M3` (`S2-4`): the pending invitation's two gestures on the bank's card. The
                // resend answers a sentence or nothing, so the pair reads alike; `renvoyerInvitation`
                // answers a result, adapted here rather than widened there (its other caller reads it).
                resendInvitation={async (invitationId) => {
                  const answer = await renvoyerInvitation(invitationId);
                  return answer.ok ? null : answer.message;
                }}
                revokeInvitation={revokeInvitationLink}
                onOpened={() => {
                  setDirectoryEpoch((epoch) => epoch + 1);
                }}
                onOpenSheet={(organisationId) => {
                  naviguer(`/partenaires/${organisationId}`);
                }}
              />
            ) : null}
            {partnerTab === "assureurs" ? <PartnerTabInWords tab="assureurs" /> : null}
            {partnerTab === "experts" ? (
              <Cabinets
                {...cabinetsDoors}
                organisations={partners?.filter((one) => one.type === "cabinet") ?? []}
              />
            ) : null}
            {partnerTab !== "concessionnaires" ? null : (
              <>
                {/* The founder's decision: the concessions as they stand, the
                    form over the veil from the tab's own « Ajouter ». */}
                <PartnerTabHeader
                  title={t("console.partenaires.concessionnairesTitre")}
                  subtitle={t("console.partenaires.concessionnairesSous")}
                  control={
                    identite.rights["ouvrirOrganisation"] === "oui"
                      ? { label: t("console.partenaires.ajouterConcessionnaire"), onClick: addDealer.show }
                      : {
                          label: t("console.partenaires.ajouterConcessionnaire"),
                          reason: t("console.partenaires.ouvrirMotif"),
                        }
                  }
                />
                <PartnerAddModal
                  open={addDealer.open}
                  title={t("console.concession.titre")}
                  onClose={addDealer.hide}
                >
                  <Partenaires
                    ouvrirConcession={ouvrirConcession}
                    onOuverte={(ouverte) => {
                      setDirectoryEpoch((epoch) => epoch + 1);
                      addDealer.hide();
                      naviguer(`/partenaires/${ouverte.id}`);
                    }}
                  />
                </PartnerAddModal>
                <Dealerships
                  partners={partners}
                  vue={vue}
                  onOpen={(opened) => {
                    naviguer(`/partenaires/${opened.id}`);
                  }}
                />
              </>
            )}
          </div>
        ) : identite === null || ecran !== "profil" ? (
          // The drawing's own sentence for a screen not yet built, its round
          // named , the landing of the two relation views today.
          <p data-testid="ecran-rond" className="text-sm text-gray-500">
            {t("console.session.ecranRond")}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <Profil
              nomComplet={identite.fullName}
              email={identite.email}
              role={identite.role}
              vue={vue}
              organisation={identite.organisation}
              droits={identite.rights}
              enregistrer={enregistrer}
              deconnecter={deconnecter}
              // `CN-B.3`: the administration's payment settings, on Profil where
              // WF-CON-43 places the administration's parameters; the block
              // exists for the right's holder alone.
              {...(identite.rights["parametrerPaiements"] === "oui"
                ? {
                    paiements: paymentSettingsDoors,
                    reservation: holdTermsDoors,
                    manquement: breachTermsDoors,
                    tolerance: mileageToleranceDoors,
                  }
                : {})}
              {...(previousScreen === null ? {} : { previousScreen })}
            />
            {/* `P0-IDN-044`. Rendered on the SERVER's flag and never on a
              build-time constant: 4.7.3 evaluates flags server side and exposes
              them on `/me`, so a founder turning the flag off makes this
              disappear on the next reload and a production console never
              receives a true to render on. Hiding it is a courtesy; the route's
              own four gates are the enforcement. */}
            {identite.flags["demo_devices_enabled"] === true ? (
              <>
                <Choreographie lireLeCode={lireLeCode} />
                {/* `P0-IDN-046`, round 191: the invitation's token beside the
                  code. Behind the SAME flag, because they are one surface: two
                  reads of two things nothing sends, and a founder who turns the
                  flag off should lose both on the next reload rather than
                  discover that one of them stayed. */}
                <InvitationCard lireLeLien={lireLeLien} />
              </>
            ) : null}
            {/* `P0-PLT-038`, rendered for every seated staff principal, and the
              reason is a FINDING rather than a preference. `platform.flags.admin`
              is an OPERATOR right, held in `identity.operator_grants`; `/me`
              answers the permission MATRIX only, so the console has no way to
              know whether this person holds it. Given that, the two honest
              choices were to widen `/me` or to render the card always and let
              the route refuse , and the second is the one that cannot fail
              closed: a diagnostic hidden behind the state it diagnoses
              disappears exactly when somebody needs it, which is how a founder
              came to stand in front of a console that could not say why a card
              was missing. The route's own right check is the enforcement, and
              the card prints the refusal in words. */}
            <DiagnosticDrapeaux lireLesDrapeaux={lireLesDrapeaux} />
          </div>
        )}
      </ConsoleShell>
    </DemandeFonctionnaliteProvider>
  );
}
